import {
  COST_CHANGE_INSIGHTS_ITEM_OPTIONS,
  COST_CHANGE_INSIGHTS_LOCATION_OPTIONS,
  COST_CHANGE_INSIGHTS_SUBMENU_DETAILS_MAP,
} from '../../../globalConfig/constants'
import { constructInitialValueStates } from '../helperFunctions'
import isEqual from 'lodash/isEqual'

export const reducer = (state, action) => {
  switch (action.type) {
    case 'update_value_states_by_field': {
      if (
        action?.field?.variableName &&
        !isEqual(state.valueStates[action.field.variableName], action.value)
      ) {
        return {
          ...state,
          valueStates: {
            ...state.valueStates,
            [action.field.variableName]: action.value,
          },
        }
      } else {
        return state
      }
    }

    case 'update_error_states': {
      return {
        ...state,
        errorStates: action.value,
        isErrorModalVisible: Object.keys(action.value).length ? true : false,
      }
    }

    case 'clear_item_value_states': {
      const emptyStates = {}
      Object.values(COST_CHANGE_INSIGHTS_ITEM_OPTIONS).forEach((field) => {
        emptyStates[field.variableName] =
          field.filterType === 'AUTOCOMPLETE' ? [] : ''
      })
      return {
        ...state,
        valueStates: {
          ...state.valueStates,
          ...emptyStates,
        },
      }
    }

    case 'update_view_by_level_value_state': {
      return {
        ...state,
        valueStates: {
          ...state.valueStates,
          [COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_TYPE
            .variableName]: action.value,
        },
      }
    }

    case 'update_view_by_values_value_state': {
      return {
        ...state,
        valueStates: {
          ...state.valueStates,
          [COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_VALUES
            .variableName]: action.value,
        },
      }
    }

    case 'reset_view_by_states': {
      return {
        ...state,
        valueStates: {
          ...state.valueStates,
          [COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_TYPE
            .variableName]: constructInitialValueStates(
            COST_CHANGE_INSIGHTS_SUBMENU_DETAILS_MAP[
              action.pathname
            ].GET_DEFAULT_SELECTIONS(action?.lanId),
          )?.[
            COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_TYPE
              .variableName
          ],
          [COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_VALUES
            .variableName]: constructInitialValueStates(
            COST_CHANGE_INSIGHTS_SUBMENU_DETAILS_MAP[
              action.pathname
            ].GET_DEFAULT_SELECTIONS(action?.lanId),
          )?.[
            COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_VALUES
              .variableName
          ],
        },
      }
    }

    case 'update_class_options': {
      return {
        ...state,
        classOptions: action.value,
      }
    }

    case 'reset_class_options': {
      return {
        ...state,
        classOptions: [],
        valueStates: {
          ...state.valueStates,
          [COST_CHANGE_INSIGHTS_ITEM_OPTIONS.DEPARTMENT_CLASS.variableName]: [],
        },
      }
    }

    case 'add_custom_clg_options': {
      return {
        ...state,
        customCLGOptions: {
          ...state.customCLGOptions,
          ...action.value,
        },
      }
    }

    case 'remove_custom_clg_options': {
      const newCustomCLGOptions = { ...state.customCLGOptions }
      delete newCustomCLGOptions[action.value]
      return {
        ...state,
        customCLGOptions: newCustomCLGOptions,
      }
    }

    case 'remove_supplier_location_options': {
      const newSupplierLocationOptions = { ...state.supplierLocationOptions }
      delete newSupplierLocationOptions[action.value]
      return {
        ...state,
        supplierLocationOptions: newSupplierLocationOptions,
      }
    }

    case 'add_supplier_location_options': {
      return {
        ...state,
        supplierLocationOptions: {
          ...state.supplierLocationOptions,
          ...action.value,
        },
      }
    }

    case 'update_error_modal_visibility': {
      return {
        ...state,
        isErrorModalVisible: action.value,
      }
    }

    case 'flip_expandable_state': {
      return {
        ...state,
        filterExpandableStates: {
          ...state.filterExpandableStates,
          [action.value]: !state.filterExpandableStates[action.value],
        },
      }
    }

    case 'update_all_expandable_states': {
      const updateValue = Object.keys(state.filterExpandableStates).some(
        (key) => state.filterExpandableStates[key],
      )
        ? false
        : true
      return {
        ...state,
        filterExpandableStates: {
          ITEM: updateValue,
          LOCATION: updateValue,
          TIMEFRAME: updateValue,
          MISC: updateValue,
        },
      }
    }

    case 'reset_value_states_to_default': {
      return {
        ...state,
        valueStates: constructInitialValueStates(
          COST_CHANGE_INSIGHTS_SUBMENU_DETAILS_MAP[
            action.pathname
          ].GET_DEFAULT_SELECTIONS(),
        ),
      }
    }

    case 'flip_get_cost_change_items_loading_status': {
      return {
        ...state,
        isGetCostChangeItemsLoading: !state.isGetCostChangeItemsLoading,
        initiateSearch: false,
      }
    }

    case 'set_cost_change_items': {
      return {
        ...state,
        costChangeItems: action.value,
        itemExpandableStates: {},
        submissionResults: {},
      }
    }

    case 'clear_cost_change_items': {
      return {
        ...state,
        costChangeItems: null,
        itemExpandableStates: {},
        submissionResults: {},
        currentPage: 1,
        recordsPerPage: 10,
      }
    }

    case 'set_cost_change_items_request_criteria': {
      return {
        ...state,
        costChangeItemsRequestCriteria: action.value,
      }
    }

    case 'flip_item_expandable_status': {
      return {
        ...state,
        itemExpandableStates: {
          ...state.itemExpandableStates,
          [action.value]: {
            isExpanded: !state.itemExpandableStates[action.value]?.isExpanded,
            currentPage:
              state.itemExpandableStates[action.value]?.currentPage || 1,
            recordsPerPage:
              state.itemExpandableStates[action.value]?.recordsPerPage || 10,
          },
        },
      }
    }

    case 'expand_all_items': {
      const newExpandableStates = {}
      state.costChangeItems?.items.forEach((costChangeItem) => {
        const key = costChangeItem?.tcin
        newExpandableStates[key] = {
          isExpanded: true,
          currentPage: state.itemExpandableStates[key]?.currentPage || 1,
          recordsPerPage: state.itemExpandableStates[key]?.recordsPerPage || 10,
        }
      })
      return {
        ...state,
        itemExpandableStates: newExpandableStates,
      }
    }

    case 'collapse_all_items': {
      const newExpandableStates = {}
      Object.keys(state.itemExpandableStates).forEach((key) => {
        newExpandableStates[key] = {
          ...state.itemExpandableStates[key],
          isExpanded: false,
        }
      })
      return {
        ...state,
        itemExpandableStates: newExpandableStates,
      }
    }

    case 'update_item_expandable_page': {
      return {
        ...state,
        itemExpandableStates: {
          ...state.itemExpandableStates,
          [action.item]: {
            ...state.itemExpandableStates[action.item],
            currentPage: action.value,
          },
        },
      }
    }

    case 'update_item_expandable_records_per_page': {
      return {
        ...state,
        itemExpandableStates: {
          ...state.itemExpandableStates,
          [action.item]: {
            ...state.itemExpandableStates[action.item],
            recordsPerPage: action.value,
            currentPage: 1,
          },
        },
      }
    }

    case 'update_submission_results': {
      return {
        ...state,
        submissionResults: {
          ...state.submissionResults,
          [action.item]: action.value,
        },
        itemExpandableStates: {
          ...state.itemExpandableStates,
          [action.item]: {
            ...state.itemExpandableStates[action.item],
            recordsPerPage:
              action.value?.pageSize ||
              state.itemExpandableStates[action.item]?.recordsPerPage,
            currentPage:
              action.value?.page ||
              state.itemExpandableStates[action.item]?.currentPage,
          },
        },
      }
    }

    case 'reset_current_page': {
      return {
        ...state,
        currentPage: 1,
        searchAfterPaginationChange: false,
      }
    }

    case 'update_current_page': {
      return {
        ...state,
        currentPage: action.value,
        searchAfterPaginationChange: true,
      }
    }

    case 'go_to_first_page': {
      return {
        ...state,
        currentPage: 1,
        searchAfterPaginationChange: true,
      }
    }

    case 'go_to_last_page': {
      return {
        ...state,
        currentPage: Math.ceil(
          state.costChangeItems?.totalResults / state.recordsPerPage,
        ),
        searchAfterPaginationChange: true,
      }
    }

    case 'go_to_next_page': {
      return {
        ...state,
        currentPage: state.currentPage + 1,
        searchAfterPaginationChange: true,
      }
    }

    case 'go_to_prev_page': {
      return {
        ...state,
        currentPage: state.currentPage - 1,
        searchAfterPaginationChange: true,
      }
    }

    case 'update_records_per_page': {
      return {
        ...state,
        recordsPerPage: action.value,
        currentPage: 1,
        searchAfterPaginationChange: true,
      }
    }

    case 'update_clg_options_to_show': {
      return {
        ...state,
        clgOptionsToShow: action.value,
      }
    }

    case 'initiate_search': {
      return {
        ...state,
        initiateSearch: true,
      }
    }

    case 'reset_to_initial_state': {
      return {
        ...initialState,
        clgOptionsToShow: state.clgOptionsToShow,
      }
    }

    default:
      return state
  }
}

export const initialState = {
  valueStates: constructInitialValueStates(),
  classOptions: [],
  customCLGOptions: {},
  supplierLocationOptions: {},
  errorStates: {},
  costChangeItems: null,
  costChangeItemsRequestCriteria: null,
  isErrorModalVisible: false,
  isGetCostChangeItemsLoading: false,
  filterExpandableStates: {
    ITEM: true,
    LOCATION: true,
    TIMEFRAME: true,
    MISC: true,
  },
  itemExpandableStates: {},
  submissionResults: {},
  currentPage: 1,
  recordsPerPage: 10,
  searchAfterPaginationChange: false,
  initiateSearch: false,
  clgOptionsToShow: 'STANDARD',
}
